import React from 'react';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import useKeepitalRoutes from '../_hook/useKeepitalRoutes';
import { ICompanySchemaPartial } from '../../../graphql/type/company';
import useLanguage from '../_hook/useLanguage';
import useAdminRoutes from '../_hook/useAdminRoutes';

interface IProps {
  company?: ICompanySchemaPartial;
  useAdminLink?: boolean;
  useIndexLink?: boolean;
  width?: number;
  height?: number;
  avatarOnly?: boolean;
  badge?: boolean;
}

const useStyles = makeStyles<Theme, IProps>((theme: Theme) => ({
  avatar: {
    width: ({ width, badge }) => (badge ? '22px' : theme.spacing(width || 5)),
    height: ({ height, badge }) =>
      badge ? '22px' : theme.spacing(height || 5),
    border: ({ badge }) =>
      badge ? `2px solid ${theme.palette.background.paper}` : 'none',
    color: theme.palette.mode === 'dark' ? 'inherit' : undefined,
  },
  avatarLink: {
    color: theme.palette.mode === 'dark' ? 'inherit' : undefined,
    textDecoration: 'none',
  },
}));

export default function SchemaCompanyLogoAvatar(props: IProps) {
  const {
    company,
    useAdminLink,
    useIndexLink,
    avatarOnly,
    badge,
    width,
    height,
  } = props;
  const classes = useStyles(props);
  const { getText } = useLanguage();
  if (!company) {
    return (
      <Tooltip title="Deleted Company">
        <Avatar className={classes.avatar}>N</Avatar>
      </Tooltip>
    );
  }

  const { path: keepitalPath } = useKeepitalRoutes();
  const { path: adminPath } = useAdminRoutes();
  const text = company.name ? getText(company.name) : '';

  const avatar =
    company &&
    company.media &&
    company.media.images &&
    company.media.images.logo
      ? company.media.images.logo.find((item) => item.status)
      : null;

  const avatarComponent = avatar ? (
    <Avatar
      className={classes.avatar}
      alt={text}
      src={avatar.filenameSmallUrl || avatar.filenameUrl}
    />
  ) : (
    <Avatar className={classes.avatar}>{text[0]}</Avatar>
  );

  if (avatarOnly) return avatarComponent;

  const avatarComponentWithTooltip = (
    <Tooltip title={text}>{avatarComponent}</Tooltip>
  );

  if ((useAdminLink || useIndexLink) && company._id) {
    let href = '#';

    if (useAdminLink) {
      href = adminPath('business.dashboard', {
        companyID: company._id as string,
      });
    } else if (company.origin) {
      href = keepitalPath('company.index', {
        origin: company.origin.toLowerCase(),
        companySlug: company.slug as string,
      });
    }

    return (
      <a
        className={classes.avatarLink}
        href={href}
        target="_blank"
        rel="noreferrer"
      >
        {avatarComponentWithTooltip}
      </a>
    );
  }

  return avatarComponentWithTooltip;
}
